/**
 * @fileoverview Categories Tab for Institution Central
 * Displays institution categories and allows requesting new ones (for approved institutions)
 */

import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Typography,
    Chip,
    Button,
    CircularProgress,
    Alert,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    Stepper,
    Step,
    StepLabel,
} from "@mui/material";
import { intl, fetchData, HTTP_STATUS, DmsPermissions } from "dmsbasereact";
import { Permissions } from "../../../constants/Permissions";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendIcon from "@mui/icons-material/Send";
import Swal from "sweetalert2";

import {
    getInstitutionCategoryLabel,
    getInstitutionCategoryOptions,
    EXCLUSIVE_CATEGORY,
} from "../../../constants/InstitutionType";
import {
    InstitutionStatusEnum,
    CATEGORIES_REQUIRING_DIRECTOR_APPROVAL,
} from "../../../constants/InstitutionStatus";

/**
 * Get approval steps for a category request
 * @param {number} category - Category value
 * @returns {Array} Array of step objects
 */
const getCategoryRequestSteps = (category) => {
    const requiresDirector = CATEGORIES_REQUIRING_DIRECTOR_APPROVAL?.includes(category);

    const steps = [
        { key: "documents", label: intl.get("Institution.Step.Documents").d("Documentos") },
        { key: "gestao_lena", label: intl.get("Institution.Step.GestaoLena").d("Gestão Lena") },
        { key: "regulatorio", label: intl.get("Institution.Step.Regulatorio").d("Regulatório") },
    ];

    if (requiresDirector) {
        steps.push({ key: "diretoria", label: intl.get("Institution.Step.Diretoria").d("Diretoria") });
    }

    steps.push({ key: "approved", label: intl.get("Institution.Step.Approved").d("Aprovado") });

    return steps;
};

/**
 * Get active step index for a category request based on status
 * @param {number} status - Request status
 * @param {number} category - Category value
 * @returns {number} Active step index
 */
const getCategoryRequestActiveStep = (status, category) => {
    const requiresDirector = CATEGORIES_REQUIRING_DIRECTOR_APPROVAL?.includes(category);

    switch (status) {
        case InstitutionStatusEnum.PENDING_DOCUMENT_UPLOAD:
            return 0;
        case InstitutionStatusEnum.PENDING_APPROVAL_GESTAO_LENA:
            return 1;
        case InstitutionStatusEnum.PENDING_APPROVAL_REGULATORIO:
            return 2;
        case InstitutionStatusEnum.PENDING_APPROVAL_DIRETORIA:
            return requiresDirector ? 3 : 2;
        case InstitutionStatusEnum.APPROVED:
            return requiresDirector ? 5 : 4;
        default:
            return 0;
    }
};

/**
 * Get step status label
 * @param {number} index - Step index
 * @param {number} activeStep - Current active step
 * @param {number} status - Request status
 * @returns {Object|null} Label info
 */
const getStepStatusLabel = (index, activeStep, status) => {
    const isCompleted = index < activeStep;
    const isActive = index === activeStep;
    const isRejected = status === InstitutionStatusEnum.REJECTED;

    if (isCompleted) {
        return { text: intl.get("Approved").d("Approved"), color: "success.main" };
    }
    if (isActive && isRejected) {
        return { text: intl.get("Rejected").d("Rejected"), color: "error.main" };
    }
    if (isActive) {
        return { text: intl.get("Pending").d("Pending"), color: "primary.main" };
    }
    return null;
};

/**
 * CategoriesTab - Displays institution categories and pending requests
 */
export const CategoriesTab = ({ institution = null,
  onRefresh = null}) => {
    const [loading, setLoading] = useState(false);
    const [categoryData, setCategoryData] = useState(null);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submittingRequestPk, setSubmittingRequestPk] = useState(null);

    const institutionPk = institution?.pk;
    const institutionStatus = institution?.status;
    const isApproved = institutionStatus === InstitutionStatusEnum.APPROVED;

    /**
     * Fetch category data for the institution
     */
    const fetchCategoryData = useCallback(async () => {
        if (!institutionPk) return;

        setLoading(true);
        setError(null);
        try {
            const response = await fetchData(`/institution/${institutionPk}/categories`, "GET", true);
            if (response.status === HTTP_STATUS.OK_200) {
                setCategoryData(response.data?.data);
            } else {
                setError(intl.get("ErrorLoadingCategories").d("Error loading categories"));
            }
        } catch (err) {
            setError(intl.get("ErrorLoadingCategories").d("Error loading categories"));
        } finally {
            setLoading(false);
        }
    }, [institutionPk]);

    useEffect(() => {
        fetchCategoryData();
    }, [fetchCategoryData]);

    /**
     * Get available categories for selection (excluding already assigned/pending)
     */
    const getAvailableForRequest = useCallback(() => {
        if (!categoryData) return [];

        const currentCategories = categoryData.current_categories || [];
        const pendingRequestCategories = (categoryData.pending_requests || []).map(r => r.category);
        const usedCategories = [...currentCategories, ...pendingRequestCategories];

        // If distributor, cannot add more
        if (categoryData.is_distributor) {
            return [];
        }

        // If has any category, cannot add distributor
        const allOptions = getInstitutionCategoryOptions();
        if (currentCategories.length > 0) {
            return allOptions.filter(
                opt => opt.value !== EXCLUSIVE_CATEGORY && !usedCategories.includes(opt.value)
            );
        }

        return allOptions.filter(opt => !usedCategories.includes(opt.value));
    }, [categoryData]);

    /**
     * Handle submitting a new category request
     */
    const handleRequestCategory = useCallback(async () => {
        if (!selectedCategory || !institutionPk) return;

        setSubmitting(true);
        try {
            const response = await fetchData(
                `/institution/${institutionPk}/category-requests`,
                "POST",
                true,
                { category: selectedCategory }
            );

            if (response.status === HTTP_STATUS.OK_200 || response.status === HTTP_STATUS.CREATED_201) {
                Swal.fire({
                    icon: "success",
                    title: intl.get("Success").d("Success"),
                    text: intl.get("CategoryRequestCreated").d("Category request created successfully"),
                });
                setDialogOpen(false);
                setSelectedCategory('');
                fetchCategoryData();
                if (onRefresh) onRefresh();
            } else {
                Swal.fire({
                    icon: "error",
                    title: intl.get("Error").d("Error"),
                    text: response.data?.detail || intl.get("ErrorCreatingRequest").d("Error creating request"),
                });
            }
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: intl.get("ErrorCreatingRequest").d("Error creating request"),
            });
        } finally {
            setSubmitting(false);
        }
    }, [selectedCategory, institutionPk, fetchCategoryData, onRefresh]);

    /**
     * Handle submitting a category request for approval
     */
    const handleSubmitRequest = useCallback(async (requestPk) => {
        if (!requestPk || !institutionPk) return;

        setSubmittingRequestPk(requestPk);
        try {
            const response = await fetchData(
                `/institution/${institutionPk}/category-requests/${requestPk}/submit`,
                "POST",
                true
            );

            if (response.status === HTTP_STATUS.OK_200 || response.status === HTTP_STATUS.CREATED_201) {
                Swal.fire({
                    icon: "success",
                    title: intl.get("Success").d("Success"),
                    text: intl.get("CategoryRequestSubmitted").d("Category request submitted for approval"),
                });
                fetchCategoryData();
                if (onRefresh) onRefresh();
            } else {
                Swal.fire({
                    icon: "error",
                    title: intl.get("Error").d("Error"),
                    text: response.data?.detail || intl.get("ErrorSubmittingRequest").d("Error submitting request"),
                });
            }
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: intl.get("ErrorSubmittingRequest").d("Error submitting request"),
            });
        } finally {
            setSubmittingRequestPk(null);
        }
    }, [institutionPk, fetchCategoryData, onRefresh]);

    if (loading && !categoryData) {
        return (
            <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    const currentCategories = categoryData?.current_categories || [];
    const pendingRequests = categoryData?.pending_requests || [];
    const availableCategories = getAvailableForRequest();
    const canRequestMore = isApproved && categoryData?.can_add_more && availableCategories.length > 0;

    return (
        <Box>
            {/* Current Categories Section */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {intl.get("CurrentCategories").d("Current Categories")}
            </Typography>

            {currentCategories.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                    {intl.get("NoCategoriesYet").d("No categories assigned yet")}
                </Typography>
            ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    {currentCategories.map((category) => (
                        <Chip
                            key={category}
                            icon={<CheckCircleIcon />}
                            label={getInstitutionCategoryLabel(category)}
                            color={category === EXCLUSIVE_CATEGORY ? "warning" : "primary"}
                            variant="filled"
                        />
                    ))}
                </Box>
            )}

            {/* Pending Requests Section */}
            {pendingRequests.length > 0 && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {intl.get("PendingCategoryRequests").d("Pending Category Requests")}
                    </Typography>

                    {pendingRequests.map((request) => {
                        const steps = getCategoryRequestSteps(request.category);
                        const activeStep = getCategoryRequestActiveStep(request.status, request.category);

                        return (
                            <Card key={request.pk} variant="outlined" sx={{ mb: 2 }}>
                                <CardContent>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                        {request.category_name || getInstitutionCategoryLabel(request.category)}
                                    </Typography>

                                    <Stepper
                                        activeStep={activeStep}
                                        alternativeLabel
                                        sx={{ mt: 2 }}
                                    >
                                        {steps.map((step, index) => {
                                            const stepStatus = getStepStatusLabel(index, activeStep, request.status);
                                            return (
                                                <Step key={step.key} completed={index < activeStep}>
                                                    <StepLabel
                                                        optional={
                                                            stepStatus ? (
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{ color: stepStatus.color, fontWeight: 500 }}
                                                                >
                                                                    {stepStatus.text}
                                                                </Typography>
                                                            ) : null
                                                        }
                                                    >
                                                        {step.label}
                                                    </StepLabel>
                                                </Step>
                                            );
                                        })}
                                    </Stepper>

                                    {request.status_justification && (
                                        <Alert severity="warning" sx={{ mt: 2 }}>
                                            <Typography variant="body2">
                                                <strong>{intl.get("Reason").d("Reason")}:</strong> {request.status_justification}
                                            </Typography>
                                        </Alert>
                                    )}

                                    {/* Submit button for document upload stage */}
                                    {request.status === InstitutionStatusEnum.PENDING_DOCUMENT_UPLOAD && (
                                        <Box sx={{ mt: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                                            <Alert severity="info" sx={{ width: "100%" }}>
                                                <Typography variant="body2">
                                                    {intl.get("CategoryRequestUploadDocsMessage").d(
                                                        "Upload any required documents in the Documents tab, then click Submit to start the approval process."
                                                    )}
                                                </Typography>
                                            </Alert>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                startIcon={submittingRequestPk === request.pk ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                                                onClick={() => handleSubmitRequest(request.pk)}
                                                disabled={submittingRequestPk !== null}
                                            >
                                                {intl.get("SubmitForApproval").d("Submit for Approval")}
                                            </Button>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })}
                </>
            )}

            {/* Distributor Warning */}
            {categoryData?.is_distributor && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    {intl.get("DistributorExclusive").d("Distributor can only be combined with Canal de Venda (Buyer)")}
                </Alert>
            )}

            {/* Request New Category Button (only for approved institutions) */}
            {canRequestMore && (
                <DmsPermissions permissions={[Permissions.MY_INSTITUTION_PAGE]}>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setDialogOpen(true)}
                        sx={{ mt: 3 }}
                        fullWidth
                    >
                        {intl.get("RequestNewCategory").d("Request New Category")}
                    </Button>
                </DmsPermissions>
            )}

            {/* Not approved message */}
            {!isApproved && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    {intl.get("CategoryRequestsRequireApproval").d(
                        "You can request additional categories after your institution is approved."
                    )}
                </Alert>
            )}

            {/* Request Category Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {intl.get("RequestNewCategory").d("Request New Category")}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {intl.get("CategoryRequestDescription").d(
                            "Select a category to add to your institution. The request will be reviewed by administrators."
                        )}
                    </Typography>

                    <FormControl fullWidth sx={{ mt: 1 }}>
                        <InputLabel>{intl.get("Category").d("Category")}</InputLabel>
                        <Select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            label={intl.get("Category").d("Category")}
                        >
                            {availableCategories.map((cat) => (
                                <MenuItem key={cat.value} value={cat.value}>
                                    {cat.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={submitting}>
                        {intl.get("Cancel").d("Cancel")}
                    </Button>
                    <Button
                        onClick={handleRequestCategory}
                        variant="contained"
                        disabled={!selectedCategory || submitting}
                    >
                        {submitting ? <CircularProgress size={20} /> : intl.get("Request").d("Request")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

CategoriesTab.propTypes = {
    institution: PropTypes.object,
    onRefresh: PropTypes.func,
};

